import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { appAnalyticsService } from '../../services/appAnalyticsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface TechUtilizationTrendChartProps {
  projectId?: string;
  businessId?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

interface TrendPoint {
  period: string;
  module: string;
  count: number;
}

const TechUtilizationTrendChart: React.FC<TechUtilizationTrendChartProps> = ({ projectId, businessId, timeRange }) => {
  const [trendData, setTrendData] = useState<TrendPoint[]>([]);
  const [moduleList, setModuleList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId && !businessId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        // Always use monthly grouping — this is a long-term trend chart
        const result = await appAnalyticsService.getUtilizationTrend({
          projectId: projectId || businessId,
          groupBy: 'month',
        });
        if (result && result.data && Array.isArray(result.data)) {
          setTrendData(result.data);
          setModuleList(result.modules || []);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch utilization trend data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [projectId, businessId, timeRange]);

  const getChartOption = () => {
    if (trendData.length === 0) return {};

    // Get unique periods sorted
    const periods = Array.from(new Set(trendData.map(d => d.period))).sort();

    // Take top 6 modules by total count to avoid clutter
    const moduleTotals = new Map<string, number>();
    trendData.forEach(d => {
      moduleTotals.set(d.module, (moduleTotals.get(d.module) || 0) + d.count);
    });
    const topModules = Array.from(moduleTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([m]) => m);

    // Build series — one line per module
    const series = topModules.map((mod, idx) => {
      const lineData = periods.map(period => {
        const point = trendData.find(d => d.period === period && d.module === mod);
        return point ? point.count : 0;
      });

      return {
        name: mod.replace(/_/g, ' '),
        type: 'line' as const,
        data: lineData,
        smooth: true,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { width: 2, color: CHART_COLORS.palette[idx % CHART_COLORS.palette.length] },
        itemStyle: { color: CHART_COLORS.palette[idx % CHART_COLORS.palette.length] },
      };
    });

    // Format period labels: "2026-02" → "Feb '26"
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const periodLabels = periods.map(p => {
      try {
        const [y, m] = p.split('-');
        return `${monthNames[parseInt(m) - 1]} '${y.slice(2)}`;
      } catch { return p; }
    });

    // Store raw periods for tooltip lookup
    const rawPeriods = periods;

    return {
      tooltip: {
        trigger: 'axis' as const,
        textStyle: { fontFamily: FONT_FAMILY, fontSize: 11 },
        formatter: (params: any) => {
          if (!params || params.length === 0) return '';
          const periodIdx = params[0].dataIndex;
          const periodLabel = periodLabels[periodIdx];
          let html = `<b>${periodLabel}</b><br/>`;
          params.forEach((p: any) => {
            if (p.value > 0) {
              html += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:5px;"></span>${p.seriesName}: <b>${p.value}</b> interactions<br/>`;
            }
          });
          return html;
        },
      },
      legend: {
        data: topModules.map(m => m.replace(/_/g, ' ')),
        bottom: 0,
        type: 'scroll' as const,
        textStyle: { fontFamily: FONT_FAMILY, fontSize: 10, color: '#718096' },
        itemWidth: 14,
        itemHeight: 3,
      },
      grid: { top: 30, right: 20, bottom: 80, left: 60 },
      xAxis: {
        type: 'category' as const,
        data: periodLabels,
        boundaryGap: false,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 9,
          color: '#718096',
          rotate: periodLabels.length > 8 ? 30 : 0,
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      yAxis: {
        type: 'value' as const,
        name: 'Interactions',
        nameLocation: 'middle' as const,
        nameGap: 40,
        nameTextStyle: { fontFamily: FONT_FAMILY, fontSize: 11, color: '#999', fontWeight: 'bold' as const },
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 10,
          color: '#718096',
        },
        splitLine: { lineStyle: { color: '#EDF2F7', type: 'dashed' as const } },
      },
      series,
    };
  };

  return (
    <ChartCard title="Tech Utilization Trend" subtitle="How module usage changes month-over-month (top 6 modules)" isLoading={isLoading} error={error}>
      {trendData.length > 0 ? (
        <ReactECharts option={getChartOption()} style={{ height: '450px', width: '100%' }} opts={{ renderer: 'svg' }} />
      ) : (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.400" fontSize="sm" fontFamily={FONT_FAMILY}>
            {!projectId && !businessId ? 'Select a project to view data' : 'No data available'}
          </Text>
        </Flex>
      )}
    </ChartCard>
  );
};

export default TechUtilizationTrendChart;
